import { Component, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { toSignal } from '@angular/core/rxjs-interop';
import { ToastService } from '../../../ui';
import { ContractsService } from '../contracts.service';
import { AuthService } from '../../../core/auth/auth.service';
import { TrialState, MAX_TRIAL_EXTENSIONS, todayIso } from '../trial-detection.util';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
} from '../../../ui';

export interface ExtendFreeTrialDialogData {
  contractId: number;
  /** Current trial end date (`YYYY-MM-DD`) or null for an open-ended trial. */
  currentEnd: string | null;
  trialState: TrialState;
  trialExtensionCount: number;
}

/** Default and bounds for the extension length, in days. */
const DEFAULT_DAYS = 14;
const MIN_DAYS = 1;
const MAX_DAYS = 28;

/**
 * Add `days` to a `YYYY-MM-DD` date, returning `YYYY-MM-DD`. Uses UTC math to
 * stay timezone-safe — a local `Date` round-tripped through `toISOString()`
 * can shift across a day boundary (Berlin is UTC+1/+2).
 */
function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

@Component({
  selector: 'cmt-extend-free-trial-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL, ...HLM_FORM_FIELD,
    HlmBadgeComponent, HlmBtnDirective, HlmIconComponent, HlmInputDirective, HlmSpinnerComponent,
  ],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucideCalendarPlus" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Extend Free Trial</h2>
          <p hlmDialogDescription>
            Contract <span class="font-mono-feat">{{ data.contractId }}</span> ·
            @if (data.trialState === 'expired') {
              <hlm-badge variant="destructive">Expired free trial</hlm-badge>
            } @else {
              <hlm-badge variant="secondary">Free trial</hlm-badge>
            }
          </p>
        </div>
        <button hlmDialogClose tabindex="-1" (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        @if (capReached()) {
          <p class="text-base text-destructive">
            This trial has already been extended {{ MAX_TRIAL_EXTENSIONS }} times and cannot be
            extended further.
          </p>
        } @else {
          <form [formGroup]="form" class="flex flex-col gap-4">
            <hlm-form-field>
              <label hlmLabel for="days">Extend by (days)</label>
              <input id="days" hlmInput type="number" formControlName="days"
                     [min]="MIN_DAYS" [max]="MAX_DAYS" step="1" />
              <p hlmFieldHint>
                Between <span class="font-mono-feat">{{ MIN_DAYS }}</span> and
                <span class="font-mono-feat">{{ MAX_DAYS }}</span> days.
                Counts from
                @if (data.trialState === 'expired') {
                  today (<span class="font-mono-feat">{{ startBase }}</span>).
                } @else {
                  the current trial end (<span class="font-mono-feat">{{ startBase }}</span>).
                }
              </p>
            </hlm-form-field>

            <div class="rounded-lg border border-border bg-muted/30 px-3 py-2 text-base">
              New trial end:
              @if (newEndDate()) {
                <strong class="font-mono-feat text-foreground">{{ newEndDate() }}</strong>
              } @else {
                <span class="text-muted-foreground">—</span>
              }
            </div>
          </form>
        }
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="submitting()">
          Cancel
        </button>
        <button hlmBtn (click)="submit()" [disabled]="!canSubmit()">
          @if (submitting()) {
            <hlm-spinner size="16" /> Extending…
          } @else {
            <hlm-icon name="lucideCalendarPlus" size="0.9rem" />
            Extend
          }
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class ExtendFreeTrialDialogComponent {
  readonly data = inject<ExtendFreeTrialDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<ExtendFreeTrialDialogComponent>);
  private readonly contractsService = inject(ContractsService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly fb = inject(FormBuilder);

  readonly MIN_DAYS = MIN_DAYS;
  readonly MAX_DAYS = MAX_DAYS;
  readonly MAX_TRIAL_EXTENSIONS = MAX_TRIAL_EXTENSIONS;

  readonly submitting = signal(false);
  readonly capReached = signal(this.data.trialExtensionCount >= MAX_TRIAL_EXTENSIONS);

  /**
   * Base date the extension is measured from:
   *   - expired trial → today (the trial restarts now)
   *   - active trial  → the current trial end (continuous), falling back to today
   *     for an open-ended trial.
   */
  readonly startBase =
    this.data.trialState === 'expired' || !this.data.currentEnd
      ? todayIso()
      : this.data.currentEnd;

  readonly form: FormGroup = this.fb.group({
    days: [DEFAULT_DAYS, [Validators.required, Validators.min(MIN_DAYS), Validators.max(MAX_DAYS)]],
  });

  private readonly daysValue = toSignal(this.form.controls['days'].valueChanges, {
    initialValue: this.form.controls['days'].value as number,
  });

  /** Computed new end date, or null when the days value is invalid (must be a whole number in range). */
  readonly newEndDate = computed(() => {
    const days = Number(this.daysValue());
    if (!Number.isInteger(days) || days < MIN_DAYS || days > MAX_DAYS) return null;
    return addDays(this.startBase, days);
  });

  canSubmit(): boolean {
    return !this.capReached() && this.form.valid && !this.submitting() && this.newEndDate() !== null;
  }

  submit(): void {
    if (!this.canSubmit()) return;
    const days = Number(this.daysValue());
    // Whole days only — the backend extends by an integer day count (1–28).
    if (!Number.isInteger(days) || days < MIN_DAYS || days > MAX_DAYS) return;

    // Guard the audit attribution: a 0 employeeId (user details not yet loaded)
    // would persist an unattributable TRIAL revision. Fail loudly instead.
    const employeeId = this.authService.currentUserId;
    if (!employeeId) {
      this.toast.open('Session not ready — please retry in a moment', 'OK', { duration: 5000, tone: 'error' });
      return;
    }

    this.submitting.set(true);
    this.contractsService
      .extendFreeTrial(this.data.contractId, days, employeeId)
      .subscribe({
        next: () => {
          this.toast.open('Free trial extended successfully', 'OK', { duration: 4000 });
          this.dialogRef.close(true);
        },
        error: (err) => {
          const msg =
            err?.status === 409
              ? 'Extension limit reached — this trial cannot be extended further'
              : 'Failed to extend free trial';
          this.toast.open(msg, 'OK', { duration: 6000, tone: 'error' });
          this.submitting.set(false);
        },
      });
  }
}
